import { Injectable } from '@angular/core';
import { LoggerService } from 'src/app/services/logger/logger.service';
import {
	SnapshotComponentStatus,
	SnapshotHardwareComponents,
	SnapshotSoftwareComponents,
	SnapshotStatus,
} from 'src/app/modules/snapshot/enums/snapshot.enum';
import { GetUpdateBaselineResponse, SnapshotInfo } from '../models/snapshot.interface';
import { HypothesisService } from 'src/app/services/hypothesis/hypothesis.service';
import { VantageShellService } from 'src/app/services/vantage-shell/vantage-shell.service';
@Injectable({
	providedIn: 'root',
})
export class SnapshotService {
	private readonly snapshotHypothesisConfigName: string = 'LE_Snapshot';
	private snapshotBridge: any;

	get addinVersion(): Promise<string> {
		return (async () => {
			let response;

			try {
				response = await this.snapshotBridge.getAddinUserVersion('2.0');
				return response.version;
			} catch (error) {
				this.loggerService.info(
					'Error, requested GetAddinVersion command version 2.0 not found.',
					error
				);
			}

			this.loggerService.error(
				'Error, no compatible GetAddinVersion command version has been found.'
			);
			return '0.0.0.0';
		})();
	}

	private pvtSnapshotInfo: SnapshotInfo;
	get snapshotInfo(): SnapshotInfo {
		return this.pvtSnapshotInfo;
	}

	private pvtSnapshotStatus: SnapshotStatus;
	get snapshotStatus(): SnapshotStatus {
		return this.pvtSnapshotStatus;
	}
	set snapshotStatus(value: SnapshotStatus) {
		this.pvtSnapshotStatus = value;
	}

	constructor(
		private loggerService: LoggerService,
		private hypothesisService: HypothesisService,
		shellService: VantageShellService
	) {
		this.snapshotBridge = shellService.getSnapshot();

		if (!this.snapshotBridge) {
			throw new Error('Error: Invalid Snapshot Bridge!');
		}

		this.initEmptySnapshot();
		this.snapshotStatus = SnapshotStatus.firstLoad;
	}

	public async getCurrentSnapshotInfo(componentName: string) {
		return this.getSnapshotInfo(componentName, false);
	}

	public async updateSnapshotInfo(componentName: string) {
		return this.getSnapshotInfo(componentName, true);
	}

	public async updateBaselineInfo(selectedComponents: Array<string>) {
		// Set component status from selectedComponents list to inProgress.
		selectedComponents.forEach((componentName) => {
			this.pvtSnapshotInfo[componentName].status = SnapshotComponentStatus.inProgress;
		});

		const payload = this.prepareUpdateBaselinePayload(selectedComponents);
		try {
			const snapshotUpdateBaselineResponse: GetUpdateBaselineResponse =
				await this.snapshotBridge.getUpdateBaseline(payload);
			if (!snapshotUpdateBaselineResponse) {
				throw Error('Could not get response from Addin');
			}

			// Iterate over snapshotInfo and set only components that had the baseline updated;
			selectedComponents.forEach((componentName) => {
				this.pvtSnapshotInfo[componentName].info =
					snapshotUpdateBaselineResponse[componentName];
				if (this.pvtSnapshotInfo[componentName].info.Items.length > 0) {
					this.pvtSnapshotInfo[componentName].status = SnapshotComponentStatus.hasData;
				} else {
					this.pvtSnapshotInfo[componentName].status = SnapshotComponentStatus.noData;
				}
			});
		} catch (error) {
			this.loggerService.error(
				`Error ${error} during request for snapshot data from UpdateBaseline`
			);

			selectedComponents.forEach((componentName) => {
				this.pvtSnapshotInfo[componentName].status = SnapshotComponentStatus.error;
			});
		}
	}

	public getAllComponentsList() {
		return this.getSoftwareComponentsList().concat(this.getHardwareComponentsList());
	}

	public getSoftwareComponentsList() {
		return Object.keys(SnapshotSoftwareComponents).filter(
			(type) => isNaN(type as any) && type !== 'values'
		);
	}

	public getHardwareComponentsList() {
		return Object.keys(SnapshotHardwareComponents).filter(
			(type) => isNaN(type as any) && type !== 'values'
		);
	}

	public anyIndividualSnapshotInProgress() {
		for (const snapshot in this.pvtSnapshotInfo) {
			if (this.pvtSnapshotInfo.hasOwnProperty(snapshot)) {
				const status = this.pvtSnapshotInfo[snapshot].status;
				if (status === SnapshotComponentStatus.inProgress) {
					return true;
				}
			}
		}
		return false;
	}

	public async isAvailable(): Promise<boolean> {
		if (this.hypothesisService) {
			const featureEnabled = await this.hypothesisService.getFeatureSetting(
				this.snapshotHypothesisConfigName
			);

			return (featureEnabled || '').toString() === 'true';
		}

		throw new Error('Hypothesis Service unavailable!');
	}

	private initEmptySnapshot() {
		this.pvtSnapshotInfo = {};

		this.getAllComponentsList().forEach((key) => {
			this.pvtSnapshotInfo[key] = {
				status: SnapshotComponentStatus.inProgress,
				info: {
					BaselineDate: '',
					LastSnapshotDate: '',
					IsDifferent: false,
					Items: [],
				},
			};
		});
	}

	private prepareUpdateBaselinePayload(data: Array<string>): SnapshotInfo {
		const snapshotInfo: SnapshotInfo = {};

		data.forEach((componentName) => {
			snapshotInfo[componentName] = this.pvtSnapshotInfo[componentName].info;
		});

		return snapshotInfo;
	}

	private async getSnapshotInfo(componentName: string, updateSnapshot: boolean): Promise<void> {
		this.pvtSnapshotInfo[componentName].status = SnapshotComponentStatus.inProgress;

		try {
			const snapshotInfoResponse = await this.snapshotBridge.getSnapshotInfo(
				componentName,
				updateSnapshot
			);
			if (!snapshotInfoResponse) {
				throw Error('Could not get response from Addin');
			}
			this.pvtSnapshotInfo[componentName].info = snapshotInfoResponse;
			if (this.pvtSnapshotInfo[componentName].info.Items.length > 0) {
				this.pvtSnapshotInfo[componentName].status = SnapshotComponentStatus.hasData;
			} else {
				this.pvtSnapshotInfo[componentName].status = SnapshotComponentStatus.noData;
			}
		} catch (error) {
			this.loggerService.error(
				`Error ${error} during request for snapshot data from ${componentName}`
			);

			// need to think better how to deal with errors
			this.pvtSnapshotInfo[componentName].status = SnapshotComponentStatus.error;
		}
	}
}
